import { type FC } from 'react';
import {
  Loader2Icon,
  CheckCircle2Icon,
  XCircleIcon,
  CircleDotIcon,
  ListIcon,
} from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { cn } from '@/lib/utils';

interface GapToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete'; reason?: string };
}

function parseArgs(args: any, argsText: string): Record<string, any> {
  try {
    return typeof args === 'object' && args ? args : JSON.parse(argsText);
  } catch {
    return {};
  }
}

function parseResult(result?: string): { success?: boolean; error?: string; data: any } {
  if (result === undefined) return { data: {} };
  try {
    const parsed = typeof result === 'string' ? JSON.parse(result) : result;
    return {
      success: parsed?.success,
      error: parsed?.error,
      data: parsed,
    };
  } catch {
    return { data: {} };
  }
}

const GAP_TYPE_LABELS: Record<string, string> = {
  missing_metric: 'Missing Metric',
  missing_dimension: 'Missing Dimension',
  missing_relationship: 'Missing Relationship',
  missing_source: 'Missing Source',
  data_quality: 'Data Quality',
  ambiguous_naming: 'Ambiguous Naming',
};

const TOOL_CONFIG: Record<string, {
  icon: FC<{ className?: string }>;
  runningLabel: (args: Record<string, any>) => string;
  completedLabel: (args: Record<string, any>) => string;
  resultLabel: (args: Record<string, any>, data: any) => string;
}> = {
  flag_semantic_gap: {
    icon: CircleDotIcon,
    runningLabel: (a) => a.title ? `Flagging gap: "${a.title}"...` : 'Flagging semantic layer gap...',
    completedLabel: (a) => a.title ? `Flagged Gap: ${a.title}` : 'Semantic Layer Gap',
    resultLabel: (_a, d) => d?.action === 'incremented' ? `Hit count updated (${d.hit_count})` : 'The implementation team will review it and be in touch.',
  },
  list_semantic_gaps: {
    icon: ListIcon,
    runningLabel: () => 'Checking existing gaps...',
    completedLabel: () => 'Semantic Layer Gaps',
    resultLabel: (_a, d) => {
      const count = d?.total ?? d?.gaps?.length ?? 0;
      return count === 0 ? 'No open gaps found.' : `${count} gap${count !== 1 ? 's' : ''} found`;
    },
  },
};

const GapToolCallComponent: FC<GapToolCallProps> = ({ toolName, args, argsText, result, status }) => {
  const isRunning = status?.type === 'running' || (status === undefined && result === undefined);
  const isError = status?.type === 'incomplete' && status.reason === 'error';
  const isComplete = status?.type === 'complete' && !isError;

  const parsedArgs = parseArgs(args, argsText);
  const { success, error: resultError, data } = parseResult(result);
  const hasError = isError || (result !== undefined && success === false) || !!resultError;

  const config = TOOL_CONFIG[toolName] || TOOL_CONFIG.flag_semantic_gap;
  const Icon = config.icon;

  return (
    <Card>
      <div className={cn(
        'p-3',
        isRunning ? 'bg-primary/10 border-b border-primary/20' : hasError ? 'bg-destructive/10' : 'bg-muted/50',
      )}>
        {isRunning ? (
          <div className="flex items-center gap-2">
            <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
            <p className="text-sm m-0 text-foreground">
              {config.runningLabel(parsedArgs)}
            </p>
          </div>
        ) : hasError ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <p className="text-sm m-0 text-muted-foreground">
                {config.completedLabel(parsedArgs)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
              <p className="text-sm m-0 text-destructive">
                {resultError || 'Operation failed'}
              </p>
            </div>
          </div>
        ) : isComplete ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <p className="text-sm m-0 text-muted-foreground">
                {config.completedLabel(parsedArgs)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
              <p className="text-sm m-0 text-foreground">
                {config.resultLabel(parsedArgs, data)}
              </p>
            </div>
            {toolName === 'flag_semantic_gap' && parsedArgs.gap_type && (
              <div className="ml-6 mt-1">
                <Badge variant="secondary" className="text-[10px] py-0 px-1.5">
                  {GAP_TYPE_LABELS[parsedArgs.gap_type] || parsedArgs.gap_type}
                </Badge>
              </div>
            )}
            {toolName === 'list_semantic_gaps' && data?.gaps?.length > 0 && (
              <div className="ml-6 mt-1 space-y-1">
                {data.gaps.slice(0, 5).map((gap: { id: string; title: string; gap_type: string; hit_count: number }) => (
                  <div key={gap.id} className="flex items-center gap-2">
                    <span className="text-xs text-foreground font-medium">{gap.title}</span>
                    <Badge variant="secondary" className="text-[10px] py-0 px-1.5">
                      {GAP_TYPE_LABELS[gap.gap_type] || gap.gap_type}
                    </Badge>
                    <span className="text-[10px] text-muted-foreground">{gap.hit_count} hits</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
            <p className="text-sm m-0 text-foreground">{config.runningLabel(parsedArgs)}</p>
          </div>
        )}
      </div>
    </Card>
  );
};

export const GapToolCall = GapToolCallComponent as React.ComponentType<any>;
